import { Component } from '@theme/component';

const STORAGE_KEY = 'theme:wishlist';
const UPDATE_EVENT = 'wishlist:update';

const HEART_ICON_SVG =
  '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M10 17.25s-6.5-4.06-6.5-8.563A3.687 3.687 0 0 1 10 6.312a3.687 3.687 0 0 1 6.5 2.375C16.5 13.19 10 17.25 10 17.25Z"/></svg>';

/**
 * @typedef {object} WishlistItem
 * @property {string} id
 * @property {string} title
 * @property {string} url
 * @property {string} image
 * @property {string} price
 */

/**
 * Reads/writes the visitor's wishlist to localStorage and notifies listeners on change.
 */
const WishlistStore = {
  /** @returns {WishlistItem[]} */
  get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },

  /** @param {string} id */
  has(id) {
    return this.get().some((item) => item.id === id);
  },

  /** @param {WishlistItem} item */
  toggle(item) {
    const items = this.get();
    const index = items.findIndex((existing) => existing.id === item.id);

    if (index === -1) {
      items.push(item);
    } else {
      items.splice(index, 1);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { items } }));

    return index === -1;
  },
};

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('.wishlist-toggle');
  if (!(button instanceof HTMLElement)) return;

  event.preventDefault();

  const id = button.dataset.productId;
  if (!id) return;

  const nowSaved = WishlistStore.toggle({
    id,
    title: button.dataset.productTitle ?? '',
    url: button.dataset.productUrl ?? '',
    image: button.dataset.productImage ?? '',
    price: button.dataset.productPrice ?? '',
  });

  for (const el of document.querySelectorAll(`.wishlist-toggle[data-product-id="${id}"]`)) {
    el.setAttribute('aria-pressed', String(nowSaved));
  }
});

/**
 * Keeps every wishlist-toggle button's pressed state in sync with storage
 * (covers buttons that were already in the DOM before a toggle happened elsewhere).
 */
function syncToggleButtons() {
  const savedIds = new Set(WishlistStore.get().map((item) => item.id));

  for (const el of document.querySelectorAll('.wishlist-toggle')) {
    if (!(el instanceof HTMLElement) || !el.dataset.productId) continue;
    el.setAttribute('aria-pressed', String(savedIds.has(el.dataset.productId)));
  }
}

document.addEventListener(UPDATE_EVENT, syncToggleButtons);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncToggleButtons);
} else {
  syncToggleButtons();
}

/**
 * The header wishlist icon: keeps its count bubble in sync with storage.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistBubbleCount
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleCount'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(UPDATE_EVENT, this.render);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(UPDATE_EVENT, this.render);
  }

  render = () => {
    const count = WishlistStore.get().length;
    this.refs.wishlistBubbleCount.textContent = count > 0 ? String(count) : '';
    this.refs.wishlistBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', count === 0);
  };
}

/**
 * The wishlist drawer body: renders saved items straight from storage (no server data source).
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLElement} itemsList
 * @property {HTMLElement} emptyState
 *
 * @extends {Component<DrawerRefs>}
 */
class WishlistDrawerComponent extends Component {
  requiredRefs = ['itemsList', 'emptyState'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(UPDATE_EVENT, this.render);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(UPDATE_EVENT, this.render);
  }

  render = () => {
    const items = WishlistStore.get();

    this.refs.itemsList.replaceChildren();
    this.refs.emptyState.classList.toggle('hidden', items.length > 0);

    for (const item of items) {
      this.refs.itemsList.append(this.#renderRow(item));
    }
  };

  /** @param {WishlistItem} item */
  #renderRow(item) {
    const row = document.createElement('div');
    row.className = 'wishlist-drawer__row';

    const link = document.createElement('a');
    link.href = item.url;
    link.className = 'wishlist-drawer__row-link';

    const image = document.createElement('img');
    image.src = item.image;
    image.alt = '';
    image.width = 64;
    image.height = 64;
    image.loading = 'lazy';
    image.className = 'wishlist-drawer__row-image';

    const info = document.createElement('span');
    info.className = 'wishlist-drawer__row-info';

    const title = document.createElement('span');
    title.className = 'wishlist-drawer__row-title';
    title.textContent = item.title;

    const price = document.createElement('span');
    price.className = 'wishlist-drawer__row-price';
    price.textContent = item.price;

    info.append(title, price);
    link.append(image, info);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'wishlist-toggle wishlist-drawer__row-remove';
    removeButton.dataset.productId = item.id;
    removeButton.setAttribute('aria-pressed', 'true');
    removeButton.setAttribute('aria-label', 'Remove from wishlist');
    removeButton.innerHTML = `<span class="svg-wrapper">${HEART_ICON_SVG}</span>`;

    row.append(link, removeButton);

    return row;
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
